import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box } from '@mui/material';

const CustomRichText = ({ value, onChange, placeholder = "Write your response here..." }) => {
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link'
  ];

  return (
    <Box sx={{ 
      mb: 1,
      '& .ql-toolbar.ql-snow': {
        border: '1px solid #e2e8f0',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
        backgroundColor: '#f8fafc',
        padding: '4px 8px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        minHeight: '32px'
      },
      '& .ql-toolbar.ql-snow .ql-formats': {
        marginRight: '6px'
      },
      '& .ql-toolbar.ql-snow .ql-picker-label': {
        fontSize: '12px',
        color: '#475569',
        padding: '2px 6px'
      },
      '& .ql-toolbar.ql-snow button': {
        padding: '2px 4px',
        width: '26px',
        height: '26px'
      },
      '& .ql-toolbar.ql-snow .ql-stroke': {
        stroke: '#64748b',
        strokeWidth: '1.5'
      },
      '& .ql-toolbar.ql-snow .ql-fill': {
        fill: '#64748b'
      },
      '& .ql-toolbar.ql-snow .ql-picker-item': {
        fontSize: '12px',
        color: '#475569'
      },
      '& .ql-toolbar.ql-snow .ql-picker-options': {
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        padding: '4px 0'
      },
      '& .ql-snow .ql-toolbar button:hover': {
        backgroundColor: 'rgba(79, 70, 229, 0.08)',
        borderRadius: '3px'
      },
      '& .ql-snow .ql-toolbar button.ql-active': {
        backgroundColor: 'rgba(79, 70, 229, 0.12)',
        borderRadius: '3px'
      },
      '& .ql-snow .ql-toolbar button.ql-active .ql-stroke': {
        stroke: '#4f46e5'
      },
      '& .ql-snow .ql-toolbar button.ql-active .ql-fill': {
        fill: '#4f46e5'
      },
      '& .ql-container.ql-snow': {
        border: '1px solid #e2e8f0',
        borderTop: 'none',
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
        minHeight: '100px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        fontSize: '13px',
        lineHeight: '1.6',
        color: '#1e293b',
        letterSpacing: '0.01em'
      },
      '& .ql-editor': {
        padding: '10px 14px',
        minHeight: '100px',
        fontSize: '13px',
        lineHeight: '1.6',
        color: '#1e293b',
        letterSpacing: '0.01em',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      },
      '& .ql-editor.ql-blank::before': {
        fontSize: '13px',
        color: '#94a3b8',
        fontStyle: 'normal',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        letterSpacing: '0.01em'
      },
      '& .ql-editor strong': {
        fontWeight: 600,
        color: '#0f172a'
      },
      '& .ql-editor em': {
        fontStyle: 'italic',
        color: '#334155'
      },
      '& .ql-editor u': {
        textDecoration: 'underline',
        textDecorationColor: '#94a3b8'
      },
      '& .ql-editor h1': {
        fontSize: '20px',
        fontWeight: 600,
        color: '#0f172a',
        marginTop: '12px',
        marginBottom: '8px',
        letterSpacing: '-0.02em'
      },
      '& .ql-editor h2': {
        fontSize: '17px',
        fontWeight: 600,
        color: '#0f172a',
        marginTop: '10px',
        marginBottom: '6px',
        letterSpacing: '-0.01em'
      },
      '& .ql-editor h3': {
        fontSize: '15px',
        fontWeight: 600,
        color: '#0f172a',
        marginTop: '8px',
        marginBottom: '4px'
      },
      '& .ql-editor ul, & .ql-editor ol': {
        paddingLeft: '20px',
        marginTop: '4px',
        marginBottom: '4px'
      },
      '& .ql-editor li': {
        padding: '2px 0',
        lineHeight: '1.6'
      },
      '& .ql-editor blockquote': {
        borderLeft: '4px solid #e2e8f0',
        paddingLeft: '14px',
        margin: '8px 0',
        color: '#475569',
        fontStyle: 'normal'
      },
      '& .ql-editor a': {
        color: '#4f46e5',
        textDecoration: 'none',
        fontWeight: 500
      },
      '& .ql-editor a:hover': {
        textDecoration: 'underline'
      },
      '& .ql-editor p': {
        marginBottom: '4px'
      },
      '& .ql-snow .ql-tooltip': {
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        borderRadius: '4px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e2e8f0',
        padding: '6px 10px'
      },
      '& .ql-snow .ql-tooltip input[type=text]': {
        fontSize: '12px',
        padding: '4px 8px',
        border: '1px solid #e2e8f0',
        borderRadius: '3px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      },
      '& .ql-snow .ql-tooltip a.ql-action, & .ql-snow .ql-tooltip a.ql-remove': {
        fontSize: '12px',
        fontWeight: 500,
        color: '#4f46e5'
      },
      '& .ql-snow .ql-tooltip a.ql-remove': {
        color: '#ef4444'
      }
    }}>
      <ReactQuill
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </Box>
  );
};

export default CustomRichText;